import type {UserPropertyADto} from '@cohort/admin-schemas/userProperty';
import AppBadge from '@cohort/merchants/apps/AppBadge';
import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import Table from '@cohort/merchants/components/Table';
import {UserPropertyDataTypeBadge} from '@cohort/merchants/components/UserPropertyDataTypeBadge';
import {useUserProperties} from '@cohort/merchants/hooks/api/UserProperties';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useBasePageFilters} from '@cohort/merchants/hooks/filters/basePageFilters';
import {
  getUserPropertiesSettingsRoute,
  getUserPropertySettingsRoute,
} from '@cohort/merchants/lib/Pages';
import CreateUserPropertyModal from '@cohort/merchants/pages/settings/user-properties/CreateUserPropertyModal';
import DeleteUserPropertyModal from '@cohort/merchants/pages/settings/user-properties/RemoveUserPropertyModal';
import {formatDate} from '@cohort/shared/utils/format';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

const PAGE_SIZE = 20;

interface UserPropertiesTableProps {
  userProperties: UserPropertyADto[];
  isLoading: boolean;
}

const UserPropertiesTable = ({
  userProperties,
  isLoading,
}: UserPropertiesTableProps): JSX.Element => {
  const {t} = useTranslation('pages', {keyPrefix: 'settings.userProperties.page'});
  const navigate = useNavigate();
  const [userPropertyToDelete, setUserPropertyToDelete] = useState<UserPropertyADto | null>(null);

  return (
    <Fragment>
      <Table
        headers={[
          {label: t('tableHeaderName')},
          {label: t('tableHeaderType')},
          {label: t('tableHeaderUserSync')},
          {label: t('tableHeaderCreatedAt')},
        ]}
        isLoading={isLoading}
        emptyState={{
          isEmpty: !isLoading && userProperties.length === 0,
          title: t('labelEmpty'),
          description: t('placeholderEmpty'),
        }}
      >
        {userProperties.map(userProperty => (
          <tr
            key={userProperty.id}
            className="h-16 cursor-pointer hover:bg-slate-50"
            onClick={() => navigate(getUserPropertySettingsRoute(userProperty.id).path)}
          >
            <td className="font-medium">{userProperty.name}</td>
            <td>
              <UserPropertyDataTypeBadge userProperty={userProperty} />
            </td>
            <td>
              {userProperty.appId !== null ? (
                <AppBadge appId={userProperty.appId} />
              ) : (
                <p className="text-slate-500">-</p>
              )}
            </td>
            <td className="text-slate-500">{formatDate(userProperty.createdAt)}</td>
            <td>
              {userProperty.appId === null && (
                <Button
                  variant="destructive"
                  onClick={e => {
                    e.stopPropagation();
                    setUserPropertyToDelete(userProperty);
                  }}
                >
                  {t('deleteButton')}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {userPropertyToDelete !== null && (
        <DeleteUserPropertyModal
          onClose={() => setUserPropertyToDelete(null)}
          userProperty={userPropertyToDelete}
        />
      )}
    </Fragment>
  );
};

const UserPropertiesSettingsPage = (): JSX.Element => {
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('pages', {keyPrefix: 'settings.userProperties.page'});

  const filters = useBasePageFilters(getUserPropertiesSettingsRoute().path, null);

  const [addModalOpened, setAddModalOpened] = useState(false);

  const {data, isLoading: isUserPropertiesLoading} = useUserProperties(merchant.id, {
    pageSize: PAGE_SIZE,
    page: filters.page,
  });
  const [pagination, userProperties] = data ?? [];

  const headerConfig = {
    title: t('title'),
    subtitle: t('subtitle'),
    topRightElements: (
      <Button
        key="add"
        onClick={() => {
          setAddModalOpened(true);
        }}
      >
        {t('addButton')}
      </Button>
    ),
  };

  const paginationConfig = {
    pagination,
    onPageChange: filters.setPage,
  };

  return (
    <AdminPage header={headerConfig} pagination={paginationConfig}>
      <UserPropertiesTable
        userProperties={userProperties ?? []}
        isLoading={isUserPropertiesLoading}
      />
      {addModalOpened && <CreateUserPropertyModal onClose={() => setAddModalOpened(false)} />}
    </AdminPage>
  );
};

export default UserPropertiesSettingsPage;
